import { Fragment } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data";
import { OmniaProjectCard } from "@/components/OmniaProjectCard";
import { MosaicProjectCard } from "@/components/MosaicProjectCard";

const reveal = {
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function Portfolio() {
    return (
        <section
            id="projects"
            className="bg-[#F3EFEA] px-6 py-24 lg:px-10 lg:py-32"
            data-testid="projects-section"
        >
            <div className="mx-auto max-w-[1400px]">
                <div className="flex flex-col justify-between gap-6 border-b border-[#121211]/10 pb-10 lg:flex-row lg:items-end">
                    <div>
                        <motion.p
                            {...reveal}
                            className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-[#6B6862]"
                        >
                            Projects
                        </motion.p>
                        <motion.h2
                            {...reveal}
                            transition={{ ...reveal.transition, delay: 0.08 }}
                            className="mt-4 font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-6xl"
                            data-testid="projects-headline"
                        >
                            Selected <span className="italic">projects</span>
                        </motion.h2>
                    </div>
                    <motion.p
                        {...reveal}
                        className="max-w-xs text-sm font-light leading-relaxed text-[#6B6862]"
                    >
                        Residences, retreats and sanctuaries across Los Angeles
                        County — each captured as one explorable experience.
                    </motion.p>
                </div>

                <OmniaProjectCard />
                <MosaicProjectCard />

                <div>
                    {PROJECTS.map((p, i) => (
                        <Fragment key={p.id}>
                        <motion.article
                            {...reveal}
                            transition={{ ...reveal.transition, delay: 0.05 * i }}
                            className="group grid items-center gap-8 border-b border-[#121211]/10 py-12 lg:grid-cols-12 lg:py-16"
                            data-testid={`project-${p.id}`}
                        >
                            <div
                                className={`overflow-hidden lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        loading="lazy"
                                        data-testid={`project-image-${p.id}`}
                                        className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.045]"
                                    />
                                </div>
                            </div>
                            <div
                                className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:text-right" : ""}`}
                            >
                                <p data-testid={`project-category-${p.id}`} className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#6B6862]">
                                    {p.category}
                                </p>
                                <h3 data-testid={`project-title-${p.id}`} className="mt-3 font-display text-3xl font-light leading-tight tracking-tight text-[#121211] transition-transform duration-500 group-hover:-translate-y-1 sm:text-4xl">
                                    {p.title}
                                </h3>
                                <p data-testid={`project-description-${p.id}`} className="mt-3 text-sm font-light text-[#121211]/70">
                                    {p.scope}
                                </p>
                                {p.tourUrl ? (
                                    <a href={p.tourUrl} target="_self" data-testid="baldwin-project-tour-link" data-interactive className="project-cta mt-6 inline-flex items-center gap-3 rounded-full border border-[#121211] bg-[#121211] px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.16em] text-[#FBF9F5]">
                                        EXPLORE 360° TOUR <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                                    </a>
                                ) : <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.26em] text-[#6B6862]">
                                    {p.location} — {p.year}
                                </p>}
                            </div>
                        </motion.article>
                        </Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
}
